import { getPosExtId } from "./utils.js";

////////////////////////////////////////////////////
///////////////////METHODS TO CALL//////////////////
////////////////////////////////////////////////////

// Set to true when the early SSR call succeeds, to prevent double analytics from the fallback.
let _ssrHandlePaymentFired = false;

/**
 * On the confirmation page: POST to the SSR handle-payment endpoint immediately,
 * before window.mealz is available, to minimise the risk of losing the analytics
 * event if the user navigates away during initialisation.
 *
 * Products are never sent: CoursesU clears their cart before the confirmation page
 * loads, and the /paiement page also returns an empty cart. SSR therefore skips
 * basket sync and goes straight to confirm. The client-side handlePayment() fires
 * as a fallback if this call fails.
 */
export async function callSsrHandlePayment(headers, ssrBaseUrl) {
  const posExtId = getPosExtId();
  if (!posExtId) return; // store_id is required by the SSR endpoint

  const url = new URL(`${ssrBaseUrl}/v3/basket/handle-payment`);
  url.searchParams.set('store_id', posExtId);

  try {
    const response = await fetch(url.toString(), {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ totalPrice: paymentTotal() }),
    });
    if (response.ok) {
      _ssrHandlePaymentFired = true;
    }
  } catch (error) {
    console.warn('[Mealz] SSR handle-payment call failed, client-side fallback will handle it', error);
  }
}

export function resetOnEmptyBasket() {
  const resetBasket = () => window.mealz.basket.reset();
  const observer = new MutationObserver(() => {
    const button = document.querySelector('[data-empty-cart-confirm]');
    // if button.offsetParent !== null means that the button is visible
    if (button && button.offsetParent !== null) {
      button.addEventListener('click', resetBasket);
    }
  });
  observer.observe(document.body, { attributes: false, childList: true, subtree: true });
}

export function handlePayment() {
  if (_ssrHandlePaymentFired) return;
  if (hasPayment()) {
    window.mealz.basketSync.handlePayment(paymentTotal());
  }
}

export function handlePaymentStarted() {
  if (location.href.includes('/paiement')) {
    if (!sessionStorage.getItem('_mealz/injector/lastUrlWasPayment')) {
      window.mealz.paymentStarted(paymentTotal());
      sessionStorage.setItem('_mealz/injector/lastUrlWasPayment', 'true');
    }
  } else {
    sessionStorage.removeItem('_mealz/injector/lastUrlWasPayment');
  }
}

// Because we don't have found any other way to listen on CoursesU's cart, we need to observe changes on their
// element that displays the current cart's price
export function listenToRetailerBasket() {
  // Get cart display on most pages
  let mutationTarget = document.getElementsByClassName("mini-cart")[0];
  if (!mutationTarget) {
    // Get cart display on /panier page
    mutationTarget = document.getElementsByClassName("cart-content-left-wrapper")[0];
  }
  // If we still haven't found the display, there is no display, which means the user doesn't have any way to
  // update the cart so we return there
  if (!mutationTarget) {
    return;
  }
  const observerOptions = { attributes: false, childList: true, subtree: true };
  const basketObserver = new MutationObserver(() => {
    // fetch the basket and send update to Mealz
    window.mealz.basketSync.retailerBasketChanged(getBasketProducts());
  });
  basketObserver.observe(mutationTarget, observerOptions);
  // Send the starting retailer basket
  window.mealz.basketSync.retailerBasketChanged(getBasketProducts());
}

export function pushProductsToBasket(retailerProducts) {
  window.partners.setQuantityOfProducts(
    retailerProducts.map(p =>({
      pid: p.id,
      // CoursesU's method sets the quantity of the product passed but our SDK send a quantity to add or remove
      // (quantity = 5 -> add 5 ; quantity = -5 -> remove 5)
      // So we need to add the current quantity of the product in their basket to our quantity (or 0 if it wasn't in the basket)
      quantity: (getBasketProducts()?.find(product => product.id === p.id)?.quantity || 0) + p.quantity
    }))
  );
}

// Get basket content and return it as an array of {id, quantity}
export function getBasketProducts() {
  return window.partners.returnCartDetails().map(p => ({ id: p.productId, quantity: p.quantity }));
}

///////////////////////////////////////////////
/////////////////USEFUL METHODS////////////////
///////////////////////////////////////////////

export function hasPayment() {
  // Check if we're on confirmation page and order amount is present
  if (isOnConfirmationPage() && hasOrderAmount()) {
    return true;
  }

  // Fallback to dataLayer check for backward compatibility
  if (Array.isArray(window.dataLayer)) {
    const paymentEvent = window.dataLayer?.find((ev) => ev[1] === "purchase");
    if (paymentEvent) {
      console.log('[Mealz] fallback to dataLayer check hasPayment');
      return true;
    }
  }
  return false;
}

export function paymentTotal() {
  // If we're on confirmation page, use order_total_amount_ati from tc_vars
  if (isOnConfirmationPage() && hasOrderAmount()) {
    return window.tc_vars.order_total_amount_ati;
  }

  // Fallback to dataLayer check for backward compatibility
  if (Array.isArray(window.dataLayer)) {
    const paymentEvent = window.dataLayer?.find((ev) => ev[1] === "purchase");
    if (paymentEvent) {
      console.log('[Mealz] fallback to dataLayer check paymentTotal');
      return paymentEvent[2].value;
    }
  }

  return window.tc_vars?.basket_amount || 0;
}

/**
 * Check if we're on the confirmation page
 * @returns {boolean} True if page_type is "e3_confirmation" (CoursesU's confirmation page)
 */
export function isOnConfirmationPage() {
  try {
    return window.tc_vars && window.tc_vars.page_type === 'e3_confirmation';
  } catch (error) {
    console.warn('[Mealz] Error checking confirmation page:', error);
    return false;
  }
}

/**
 * Check if order amount is present in tc_vars
 * @returns {boolean} True if order_total_amount_ati is present and valid (order amount all taxes included)
 */
export function hasOrderAmount() {
  try {
    return window.tc_vars && 
           window.tc_vars.order_total_amount_ati && 
           typeof window.tc_vars.order_total_amount_ati === 'number' && 
           window.tc_vars.order_total_amount_ati > 0;
  } catch (error) {
    console.warn('[Mealz] Error checking order amount:', error);
    return false;
  }
}
